import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  Check,
  Link2,
  Loader2,
  LogOut,
  Plus,
  Settings,
  User,
  X,
} from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import { directoryRoleLabel } from "@/components/Directory/roleLabels";
import { signOut, useSession } from "@/auth/SessionProvider";
import { apiGet, apiPost } from "@/lib/apiClient";
import { resetDirectoryOrgContextCache } from "@/lib/ensureDirectoryOrgContext";
import { postSessionOrgUpdate } from "@/lib/directorySessionOrg";
import { writeDashboardOrgCookie } from "@/lib/dashboardOrg";
import { cn } from "@/lib/utils";

type OrgRow = {
  orgId: string;
  domain: string;
  companyName: string;
  displayEmail?: string | null;
  role?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userLabel: string;
  userImageSrc?: string | null;
};

function isOrgId(orgId: string): boolean {
  return /^\d{7}$/.test(orgId.trim());
}

export function DirectoryOrgPanel({ open, onOpenChange, userLabel, userImageSrc }: Props) {
  const navigate = useNavigate();
  const session = useSession();
  const state = useDirectoryState();
  const ready = state.status === "ready" ? state.value : null;
  const sessionOrgId =
    session.data?.user?.orgId?.trim() ||
    ready?.org?.orgId?.trim() ||
    "";
  const canManage = ready?.canManageUsers === true;

  const [orgs, setOrgs] = useState<OrgRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const [mode, setMode] = useState<"list" | "connect" | "create">("list");

  const [connectEmail, setConnectEmail] = useState("");
  const [connectPassword, setConnectPassword] = useState("");
  const [connectDomain, setConnectDomain] = useState("");
  const [connectBusy, setConnectBusy] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const [createName, setCreateName] = useState("");
  const [createDomain, setCreateDomain] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const loadOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<{ orgs?: OrgRow[] }>("/api/me/orgs");
      if (res.ok && res.data?.orgs) setOrgs(res.data.orgs);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setMode("list");
    setSwitchError(null);
    void loadOrgs();
  }, [open, loadOrgs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const switchToOrg = async (orgId: string) => {
    if (!isOrgId(orgId) || orgId === sessionOrgId) return;
    setSwitchError(null);
    try {
      writeDashboardOrgCookie(orgId);
      await postSessionOrgUpdate(orgId);
      resetDirectoryOrgContextCache();
      session.refresh();
      onOpenChange(false);
      window.location.assign("/");
    } catch (error) {
      setSwitchError(error instanceof Error ? error.message : "Could not switch organization.");
    }
  };

  const submitConnect = async () => {
    setConnectBusy(true);
    setConnectError(null);
    try {
      const res = await apiPost<{ org?: { orgId: string }; error?: string }>("/api/me/orgs/connect", {
        email: connectEmail.trim(),
        password: connectPassword,
        organizationDomain: connectDomain.trim() || undefined,
        profileSource: "global",
        sourceOrgId: sessionOrgId || undefined,
      });
      if (!res.ok || !res.data?.org?.orgId) {
        setConnectError(res.error || res.data?.error || "Could not connect that organization.");
        return;
      }
      setConnectEmail("");
      setConnectPassword("");
      setConnectDomain("");
      setMode("list");
      await switchToOrg(res.data.org.orgId);
    } catch {
      setConnectError("Could not connect that organization.");
    } finally {
      setConnectBusy(false);
    }
  };

  const submitCreate = async () => {
    setCreateBusy(true);
    setCreateError(null);
    try {
      const res = await apiPost<{ org?: { orgId: string }; error?: string }>("/api/me/orgs/create", {
        companyName: createName.trim(),
        domain: createDomain.trim(),
        profileSource: "global",
        sourceOrgId: sessionOrgId || undefined,
      });
      if (!res.ok || !res.data?.org?.orgId) {
        setCreateError(res.error || res.data?.error || "Could not create that organization.");
        return;
      }
      setCreateName("");
      setCreateDomain("");
      setMode("list");
      await switchToOrg(res.data.org.orgId);
    } catch {
      setCreateError("Could not create that organization.");
    } finally {
      setCreateBusy(false);
    }
  };

  if (!open) return null;

  const initials = userLabel
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!)
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const listed = orgs.filter((row) => row.orgId && isOrgId(row.orgId));
  const fieldClass =
    "mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-white/20 dark:bg-white/5 dark:text-white";

  return (
    <div className="fixed inset-0 z-[60] flex justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-gray-900/50"
        aria-label="Close organization panel"
        onClick={() => onOpenChange(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="directory-org-sheet-title"
        className="relative flex h-full w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl dark:border-white/10 dark:bg-gray-900"
      >
        <div className="flex items-start justify-between border-b border-gray-200 px-4 py-4 dark:border-white/10">
          <div>
            <h2 id="directory-org-sheet-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Organization
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Work in one organization at a time. Switch without signing in again.
            </p>
          </div>
          <button
            type="button"
            className="rounded-md p-1 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-white/10 dark:bg-white/5">
            {userImageSrc ? (
              <img src={userImageSrc} alt="" className="size-10 shrink-0 rounded-full object-cover" />
            ) : (
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
                {initials || "A"}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-gray-900 dark:text-white">{userLabel}</p>
              {sessionOrgId ? (
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  Current org id: <span className="font-mono">{sessionOrgId}</span>
                </p>
              ) : ready?.org?.orgId ? (
                <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                  Working in {ready.org.companyName || ready.org.domain}{" "}
                  <span className="font-mono">({ready.org.orgId})</span>
                </p>
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400">No org in session</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {canManage ? (
                <button
                  type="button"
                  className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
                  aria-label="Security"
                  onClick={() => {
                    onOpenChange(false);
                    navigate("/security");
                  }}
                >
                  <Settings className="size-5" />
                </button>
              ) : null}
              <ThemeToggle />
              <button
                type="button"
                className="rounded-md p-2 text-gray-500 transition-colors hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-white/10"
                aria-label="Your profile"
                onClick={() => {
                  onOpenChange(false);
                  navigate("/");
                }}
              >
                <User className="size-5" />
              </button>
            </div>
          </div>

          <button
            type="button"
            className="mt-3 flex w-full items-center gap-2 rounded-md border border-gray-200 px-3 py-2.5 text-sm font-medium text-red-600 transition-colors hover:bg-red-50 dark:border-white/10 dark:text-red-400 dark:hover:bg-red-950/30"
            onClick={() => {
              onOpenChange(false);
              signOut();
            }}
          >
            <LogOut className="size-4 shrink-0" aria-hidden />
            Sign out
          </button>

          {mode === "list" ? (
            <div className="mt-6 flex flex-col gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Switch organization
              </p>
              {switchError ? <p className="text-sm text-red-600 dark:text-red-400">{switchError}</p> : null}
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="size-8 animate-spin text-gray-400" />
                </div>
              ) : listed.length === 0 ? (
                <div
                  className="flex w-full items-center gap-3 rounded-md border border-sky-300 bg-sky-50 px-3 py-2.5 text-left text-sm dark:border-sky-800 dark:bg-sky-950/40"
                >
                  <Building2 className="size-4 shrink-0 text-gray-500" aria-hidden />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium text-gray-900 dark:text-white">
                      {ready?.org?.companyName || ready?.org?.domain || "Organization"}
                      {ready?.me?.role ? (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {directoryRoleLabel(ready.me.role)}
                        </span>
                      ) : null}
                    </span>
                    {sessionOrgId ? (
                      <span className="block truncate font-mono text-xs text-gray-500">{sessionOrgId}</span>
                    ) : null}
                  </span>
                  <Check className="size-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
                </div>
              ) : (
                <ul className="space-y-1">
                  {listed.map((row) => {
                    const active = sessionOrgId === row.orgId;
                    return (
                      <li key={row.orgId}>
                        <button
                          type="button"
                          onClick={() => void switchToOrg(row.orgId)}
                          className={cn(
                            "flex w-full items-center gap-3 rounded-md border px-3 py-2.5 text-left text-sm transition-colors",
                            active
                              ? "border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40"
                              : "border-transparent hover:bg-gray-100 dark:hover:bg-white/5",
                          )}
                        >
                          <Building2 className="size-4 shrink-0 text-gray-500" aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-gray-900 dark:text-white">
                              {row.companyName || row.domain}
                              {row.role ? (
                                <span className="ml-2 text-xs font-normal text-gray-500">
                                  {directoryRoleLabel(row.role)}
                                </span>
                              ) : null}
                            </span>
                            <span className="block truncate font-mono text-xs text-gray-500">
                              {row.domain} · {row.orgId}
                            </span>
                            {row.displayEmail ? (
                              <span className="block truncate text-xs text-gray-500">as {row.displayEmail}</span>
                            ) : null}
                          </span>
                          {active ? (
                            <Check className="size-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              <div className="mt-4 flex flex-col gap-2 border-t border-gray-200 pt-4 dark:border-white/10">
                <button
                  type="button"
                  className="flex items-center justify-start rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:text-gray-100 dark:hover:bg-white/5"
                  onClick={() => {
                    setConnectError(null);
                    setMode("connect");
                  }}
                >
                  <Link2 className="mr-2 size-4" aria-hidden />
                  Connect an organization
                </button>
                <button
                  type="button"
                  className="flex items-center justify-start rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:text-gray-100 dark:hover:bg-white/5"
                  onClick={() => {
                    setCreateError(null);
                    setMode("create");
                  }}
                >
                  <Plus className="mr-2 size-4" aria-hidden />
                  Create organization
                </button>
              </div>
            </div>
          ) : mode === "connect" ? (
            <form
              className="mt-6 flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                void submitConnect();
              }}
            >
              <button
                type="button"
                onClick={() => setMode("list")}
                className="flex items-center gap-1 self-start text-xs font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <ArrowLeft className="size-3.5" aria-hidden /> Back
              </button>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Connect an organization</p>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Sign in once with the account you use for the other organization. After that you can
                switch to it from this panel.
              </p>
              <label className="block text-sm text-gray-900 dark:text-gray-100">
                <span className="font-medium">Email</span>
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={connectEmail}
                  onChange={(e) => setConnectEmail(e.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="block text-sm text-gray-900 dark:text-gray-100">
                <span className="font-medium">Password</span>
                <input
                  type="password"
                  required
                  autoComplete="current-password"
                  value={connectPassword}
                  onChange={(e) => setConnectPassword(e.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="block text-sm text-gray-900 dark:text-gray-100">
                <span className="font-medium">Organization domain (optional)</span>
                <input
                  value={connectDomain}
                  onChange={(e) => setConnectDomain(e.target.value)}
                  placeholder="other-org.com"
                  className={fieldClass}
                />
              </label>
              {connectError ? <p className="text-sm text-red-600 dark:text-red-400">{connectError}</p> : null}
              <button
                type="submit"
                disabled={connectBusy}
                className="inline-flex h-9 items-center justify-center rounded-md bg-gray-900 px-3 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-gray-900"
              >
                {connectBusy ? "Connecting…" : "Connect"}
              </button>
            </form>
          ) : (
            <form
              className="mt-6 flex flex-col gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                void submitCreate();
              }}
            >
              <button
                type="button"
                onClick={() => setMode("list")}
                className="flex items-center gap-1 self-start text-xs font-semibold text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"
              >
                <ArrowLeft className="size-3.5" aria-hidden /> Back
              </button>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">Create organization</p>
              <label className="block text-sm text-gray-900 dark:text-gray-100">
                <span className="font-medium">Company name</span>
                <input
                  required
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  className={fieldClass}
                />
              </label>
              <label className="block text-sm text-gray-900 dark:text-gray-100">
                <span className="font-medium">Domain</span>
                <input
                  required
                  value={createDomain}
                  onChange={(e) => setCreateDomain(e.target.value)}
                  placeholder="company.com"
                  className={fieldClass}
                />
              </label>
              {createError ? <p className="text-sm text-red-600 dark:text-red-400">{createError}</p> : null}
              <button
                type="submit"
                disabled={createBusy}
                className="inline-flex h-9 items-center justify-center rounded-md bg-gray-900 px-3 text-sm font-semibold text-white disabled:opacity-50 dark:bg-white dark:text-gray-900"
              >
                {createBusy ? "Creating…" : "Create"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
